import { Form } from "@inertiajs/react"
import { X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-32 — Brand-side `Reject application` confirm dialog (UX Spec §2.5).
 *
 * Wraps Inertia `<Form>` (Crew rule) — no shadcn Form, no RHF, no zod.
 * Hidden `event=reject_application` input drives the controller
 * dispatch; rejected is terminal in v1 (no `reject_reason` field —
 * PRD §1 + Open Question 5; V2 may add).
 *
 * Default focus on **Cancel** per UX Spec §6 #9 — destructive default
 * (rejection is irreversible). Trigger button uses outline-destructive
 * styling; the confirm button is solid destructive.
 */
interface RejectApplicationConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function RejectApplicationConfirmDialog({
  row,
  missionId,
}: RejectApplicationConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={t("collabs.reject_application.aria", {
              title: ariaTitle,
            })}
            data-testid="brand-mission-collab-reject-application-trigger"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <X aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.reject_application.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-reject-application-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.reject_application.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.reject_application.body")}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="reject_application" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  autoFocus
                  data-testid="brand-mission-collab-reject-application-cancel"
                >
                  {t("collabs.reject_application.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={processing}
                  data-testid="brand-mission-collab-reject-application-confirm"
                >
                  {t("collabs.reject_application.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
